import AddCircleIcon from "@mui/icons-material/AddCircle";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import CancelIcon from "@mui/icons-material/Cancel";
import CloseIcon from "@mui/icons-material/Close";

import { DragDropContext, Draggable, Droppable, DropResult } from "@hello-pangea/dnd";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
  useTheme
} from "@mui/material";
import PercentageIcon from "assets/icons/percentage.svg";
import { Field, Form, Formik } from "formik";
import { awsImageRootUrl } from "lib/constants";
import { Category, fetchCategory } from "lib/features/category/categoryService";
import { useGetIndustryTypesQuery } from "lib/features/industries/industriesService";
import { Industry } from "lib/features/industries/types";
import { useUpadateItemMutation } from "lib/features/item/itemService";
import {
  CreateAdditionalOptionDto,
  CreateItemWithDetailDto,
  Item,
} from "lib/features/item/types";
import { useUploadImageMutation } from "lib/features/s3/s3Service";
import { UploadImageRequest } from "lib/features/s3/types";
import { useEffect, useRef, useState } from "react";
import Cropper from "react-easy-crop";
import { useNavigate } from "react-router-dom";
import { getCroppedImage } from "util/cropImage";
import { v4 as uuid } from "uuid";
import AdOnCheckBoxModal from "../NewItemForm/components/AdOnCheckBoxModal";
import AdOnDropdownModal from "../NewItemForm/components/AdOnDropdownModal";
import AdOnTextModal from "../NewItemForm/components/AdOnTextModal";
import Label from "../NewItemForm/components/Label";
import PostScript from "../NewItemForm/components/PostScript";
// import editFormValidation from "./EditItem.schema";
import { extractHoursAndMinutes, formatDuration } from "util/commonHelper";
import DurationPicker from "../NewItemForm/components/DurationPicker";
import { editFormValidation } from "./EditItem.schema";
import SaveItemModal from "./SaveItemModal";

interface EditItemFormProps {
  itemDetails: Item;
  itemId: number;
}

const EditItemForm = ({ itemDetails, itemId }: EditItemFormProps) => {
  const theme = useTheme();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const navigate = useNavigate();
  const { data: industries } = useGetIndustryTypesQuery(null, {});
  const [uploadImage] = useUploadImageMutation();
  const [updateItem] = useUpadateItemMutation();
  const [categories, setCategories] = useState<Category[]>([]);
  const [filteredCategories, setFilteredCategories] = useState<Category[]>([]);
  const [selectedIndustry, setSelectedIndustry] = useState<number>(
    itemDetails?.industries[0]?.id
  );

  // Cropper states
  const [openCropper, setOpenCropper] = useState(false);
  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<any>(null);
  const [currentFieldValue, setCurrentFieldValue] = useState<any>(null);

  const [visibleCheckboxModal, setVisibleCheckboxModal] =
    useState<boolean>(false);
  const [visibleTextModal, setVisibleTextModal] = useState<boolean>(false);
  const [visibleDropdownModal, setVisibleDropdownModal] =
    useState<boolean>(false);
  const [visibleSaveModal, setVisibleSaveModal] = useState<boolean>(false);
  const [loader, setLoader] = useState<boolean>(false);

  const duration = itemDetails?.itemDetails?.[0]?.duration;
  const extractedDuration = extractHoursAndMinutes(
    duration !== null && duration !== undefined ? duration.toString() : null
  );

  const [formValues, setFormValues] = useState<CreateItemWithDetailDto>({
    businessId: itemDetails?.business?.id,
    item_type: itemDetails?.item_type,
    item_name: itemDetails?.itemDetails[0]?.item_name,
    headline: itemDetails?.itemDetails[0]?.headline,
    description: itemDetails?.itemDetails[0]?.description,
    whats_included: itemDetails?.itemDetails[0]?.whats_included,
    cancellation_policy: itemDetails?.itemDetails[0]?.cancellation_policy,
    unit_name: itemDetails?.itemDetails[0]?.unit_name,
    price: itemDetails?.itemDetails[0]?.price,
    capacity: itemDetails?.itemDetails[0]?.capacity,
    price_description: itemDetails?.itemDetails[0]?.price_description,
    comission: itemDetails?.itemDetails[0]?.comission,
    additional_info: itemDetails?.itemDetails[0]?.additional_info,
    offering: itemDetails?.itemDetails[0]?.offering,
    min_age: itemDetails?.itemDetails[0]?.min_age ?? null,
    max_age: itemDetails?.itemDetails[0]?.max_age ?? null,
    max_guest: itemDetails?.itemDetails[0]?.max_guest ?? null,
    images: itemDetails?.itemImages?.map((image) => image.uri) ?? [],
    industry_ids: itemDetails?.industries?.map((industry) => industry.id) || [],
    category_ids: itemDetails?.categories?.map((category) => category.id) || [],
    additionalOptions: itemDetails?.additionalOptions || [],
    duration_applicable: itemDetails?.duration_applicable ?? true,
    duration_hours: extractedDuration.duration_hours,
    duration_minutes: extractedDuration.duration_minutes,
    selling_points: itemDetails?.selling_points ?? "",
  });

  const calculatePayoffPrice = (
    price: number,
    commissionRate: string | number
  ): string => {
    if (price < 1) {
      return "Please set the price first.";
    }
    if (!commissionRate) {
      return "Please set a commission rate.";
    }

    const platformFee = (price * 5) / 100;
    const priceAfterPlatformFee = (price - platformFee).toFixed(2);

    const commissionPercentage =
      typeof commissionRate === "string"
        ? parseFloat(commissionRate)
        : commissionRate;
    const commissionAmount =
      (price * commissionPercentage) / 100;
    const payoffPrice = `$${(
      parseFloat(priceAfterPlatformFee) - commissionAmount
    ).toFixed(2)}`;
    return payoffPrice;
  };

  const onSubmit = (values: CreateItemWithDetailDto) => {
    setVisibleSaveModal(true);
  };

  const handleEditSubmit = async () => {
    try {
      setLoader(true);

      let body = {
        ...formValues,
        duration_applicable: formValues.duration_applicable ?? true,
        duration: formValues.duration_applicable
          ? formatDuration(
            formValues.duration_hours,
            formValues.duration_minutes
          )
          : undefined,
      };

      await updateItem({ id: itemId, data: body });
      navigate("/my-items");
    } catch (error) {
      console.log("Error updating item:", error);
    } finally {
      setLoader(false);
      setVisibleSaveModal(false);
    }
  };

  const onCropComplete = (croppedArea: any, croppedAreaPixels: any) => {
    setCroppedAreaPixels(croppedAreaPixels);
  };

  const handleCropConfirm = async (setFieldValue: any) => {
    if (!imageSrc || !croppedAreaPixels) return;
    try {
      const croppedBase64 = await getCroppedImage(imageSrc, croppedAreaPixels);
      if (croppedBase64) {
        const response = await resizeAndUploadImage(croppedBase64);
        if (response) {
          const imageUrl = `${response}`;

          // Update Formik values
          const updatedImages = [...currentFieldValue.images, imageUrl];
          setFieldValue("images", updatedImages);

          // Update global state
          setFormValues((prevValues: any) => ({
            ...prevValues,
            images: updatedImages,
          }));
        }
      }
    } catch (err) {
      console.error("Crop/Upload failed:", err);
    } finally {
      setOpenCropper(false);
    }
  };

  const resizeAndUploadImage = async (image: string) => {
    return new Promise((resolve, reject) => {
      const id = uuid() + ".jpg";
      const canvas = canvasRef.current;
      const ctx = canvas?.getContext("2d");

      if (ctx && canvas) {
        const img = new Image();
        img.onload = async () => {
          const targetWidth = 1080;
          const scaleFactor = targetWidth / img.width;
          const targetHeight = img.height * scaleFactor;

          canvas.width = targetWidth;
          canvas.height = targetHeight;

          ctx.clearRect(0, 0, targetWidth, targetHeight);
          ctx.drawImage(img, 0, 0, targetWidth, targetHeight);

          // Convert canvas image to base64
          const base64String = canvas.toDataURL("image/jpeg").split(",")[1];
          const fileSize = atob(base64String).length;

          const payload: UploadImageRequest = {
            id,
            image: {
              base64: base64String,
              width: targetWidth,
              height: targetHeight,
              fileSize,
              uri: "",
              type: "image/jpeg",
              fileName: id,
            },
          };

          try {
            const result = await uploadImage(payload);
            resolve(result?.data);
          } catch (error) {
            console.log("Upload error:", error);
            reject(error);
          }
        };

        img.onerror = (err) => reject("Error loading image.");
        img.src = image;
      } else {
        reject("Canvas context is not available.");
      }
    });
  };

  useEffect(() => {
    const getCategories = async () => {
      try {
        const categories = await fetchCategory();
        setCategories(categories);
      } catch (error) { }
    };

    getCategories();
  }, []);

  useEffect(() => {
    if (selectedIndustry && categories) {
      let newCategories = categories?.filter(
        (category: Category) =>
          category?.category !== "All" &&
          (category?.category_type === selectedIndustry ||
            category?.category_type === 0)
      );
      setFilteredCategories(newCategories);
    }
  }, [categories, selectedIndustry]);

  return (
    <Box sx={{ marginTop: "15px" }}>
      <Typography variant="h1" component="h1">
        Edit Details
      </Typography>
      <Typography
        sx={{
          color: "gray",
          fontSize: "0.8rem",
        }}
      >
        *Required fields
      </Typography>

      {/* Form */}

      <Formik
        initialValues={formValues}
        validationSchema={editFormValidation}
        enableReinitialize={true}
        onSubmit={(values) => onSubmit(values)}
      >
        {({ values, errors, touched, setFieldValue }) => {
          // Store current field value for cropper
          setCurrentFieldValue(values);

          const handleChange = (e: React.ChangeEvent<any>) => {
            const { name, value } = e.target;
            setFieldValue(name, value);
            setFormValues((prevValues: any) => ({
              ...prevValues,
              [name]: value,
            }));
          };

          const handleNumericChange = (
            e: React.ChangeEvent<any>,
            fieldName: string
          ) => {
            const { value } = e.target;
            if (/^\d*$/.test(value) || value === "") {
              const numericValue = value === "" ? null : Number(value);

              setFieldValue(fieldName, numericValue);
              setFormValues((prevValues: any) => ({
                ...prevValues,
                [fieldName]: numericValue,
              }));
            }
          };

          const handleOptionSave = (data: {
            header: string;
            type: number; // 1: Text Field, 2: Dropdown, 3: Checkbox
            required: boolean;
            place_holder?: string;
            price?: number;
            availability?: number;
            description?: string;
          }) => {
            const newOption: CreateAdditionalOptionDto = {
              header: data.header,
              required: data.required,
              type: Number(data.type),
              place_holder: data.place_holder,
              description: data.description,
              price: Number(data.price),
              availability: Number(data.availability),
            };

            setFieldValue("additionalOptions", [
              ...(values.additionalOptions || []),
              newOption,
            ]);

            setFormValues((prevValues: any) => ({
              ...prevValues,
              additionalOptions: [
                ...(prevValues.additionalOptions || []),
                newOption,
              ],
            }));
          };

          const handleRemoveAdditionalOption = (index: number) => {
            if (values?.additionalOptions) {
              const updatedAdditionalOptions: CreateAdditionalOptionDto[] =
                values?.additionalOptions?.filter(
                  (_: CreateAdditionalOptionDto, idx: number) => idx !== index
                );
              setFieldValue("additionalOptions", updatedAdditionalOptions);
              setFormValues((prevValues: any) => ({
                ...prevValues,
                additionalOptions: updatedAdditionalOptions,
              }));
            }
          };

          const handleSelectIndustryCategory = (industry_id: number) => {
            setSelectedIndustry(industry_id);
            if (!values.industry_ids.includes(industry_id)) {
              setFieldValue("industry_ids", [
                ...values.industry_ids,
                industry_id,
              ]);

              setFormValues((prevValues: any) => ({
                ...prevValues,
                industry_ids: [...prevValues.industry_ids, industry_id],
              }));
            }
          };

          const handleSelectCategory = (category_id: number) => {
            const isCategorySelected =
              values.category_ids.includes(category_id);

            if (isCategorySelected) {
              const newCategoryIds = values.category_ids.filter(
                (id) => id !== category_id
              );
              setFieldValue("category_ids", newCategoryIds);

              setFormValues((prevValues: any) => ({
                ...prevValues,
                category_ids: newCategoryIds,
              }));
            } else {
              setFieldValue("category_ids", [
                ...values.category_ids,
                category_id,
              ]);

              setFormValues((prevValues: any) => ({
                ...prevValues,
                category_ids: [...prevValues.category_ids, category_id],
              }));
            }
          };

          const handleImageUpload = async (
            event: React.ChangeEvent<HTMLInputElement>
          ) => {
            const file = event.target.files?.[0];
            if (file && file.type.startsWith("image/")) {
              const reader = new FileReader();
              reader.onload = async (e: ProgressEvent<FileReader>) => {
                if (e.target?.result) {
                  // Open cropper with the raw image
                  setImageSrc(e.target.result as string);
                  setOpenCropper(true);
                }
              };
              reader.readAsDataURL(file);
            }
          };

          const handleDragEnd = (result: DropResult) => {
            if (!result.destination) return;

            const reordered = Array.from(values.images);
            const [moved] = reordered.splice(result.source.index, 1);
            reordered.splice(result.destination.index, 0, moved);

            setFieldValue("images", reordered);
            setFormValues((prevValues: any) => ({
              ...prevValues,
              images: reordered,
            }));
          };

          const handleRemoveImage = (index: number) => {
            const newFormImages = (values.images || []).filter(
              (_: string, i: number) => i !== index
            );
            setFieldValue("images", newFormImages);
            setFormValues((prevValues: any) => ({
              ...prevValues,
              images: newFormImages,
            }));
          };

          const handleDurationChange = (
            hours: number | null,
            minutes: number | null
          ) => {
            setFieldValue("duration_hours", hours);
            setFieldValue("duration_minutes", minutes);

            setFormValues((prevValues: any) => ({
              ...prevValues,
              duration_hours: hours,
            }));
            setFormValues((prevValues: any) => ({
              ...prevValues,
              duration_minutes: minutes,
            }));
          };

          const handleDurationApplicableChange = (checked: boolean) => {
            setFieldValue("duration_applicable", checked);
            setFormValues((prevValues: any) => ({
              ...prevValues,
              duration_applicable: checked,
            }));

            if (checked) {
              setFieldValue("duration_hours", null);
              setFormValues((prevValues: any) => ({
                ...prevValues,
                duration_hours: null,
              }));
              setFieldValue("duration_minutes", null);
              setFormValues((prevValues: any) => ({
                ...prevValues,
                duration_minutes: null,
              }));
            }
          };

          return (
            <Form>
              <Box sx={{ fontFamily: theme.typography.fontFamily }}>
                {/* Item Name */}
                <Box>
                  <Label label="Item Name" required={true} />
                  <Field
                    as={TextField}
                    name="item_name"
                    fullWidth
                    onChange={handleChange}
                    error={touched.item_name && Boolean(errors.item_name)}
                    multiline
                    minRows={1}
                    maxRows={2}
                  />
                  {errors.item_name && touched.item_name && (
                    <Typography color="red" fontSize="12px">
                      {errors.item_name}
                    </Typography>
                  )}
                  <PostScript
                    text="Item name should be brief and descriptive."
                    italics='Ex: "One Hour Guided Jet Ski Tour"'
                  />
                </Box>

                {/* Headline */}
                <Box>
                  <Label label="Headline" required={true} />
                  <Field
                    as={TextField}
                    name="headline"
                    fullWidth
                    onChange={handleChange}
                    error={touched.headline && Boolean(errors.headline)}
                    inputProps={{ maxLength: 85 }}
                  />
                  
                    <Box>
                      {errors.headline && touched.headline && (
                        <Typography color="red" fontSize="12px">
                          {errors.headline}
                        </Typography>
                      )}
                    </Box>
                    <Typography
                      fontSize="12px"
                      color={values.headline?.length >= 85 ? "red" : "gray"}
                      fontWeight={values.headline?.length >= 85 ? "normal" : "normal"}
                      display={values.headline?.length >= 85 ? "" : "none"} 
                    >
                      Headline must not exceed 85 characters.
                    </Typography>
                  
                  <PostScript
                    text="List the most important details in 85 characters"
                    italics="Ex: $1000 | 32 passengers | BYOB | coolers+ice included"
                  />
                </Box>

                {/* Item Images */}
                <Box>
                  <Typography
                    variant="h5"
                    sx={{ fontWeight: "bold", marginTop: "1rem" }}
                  >
                    Item Photos *
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "0.9rem",
                      marginBottom: "0.5rem",
                      marginTop: "-0.5rem",
                    }}
                  >
                    Upload up to four photos of your item.
                  </Typography>

                  <Box sx={{ marginTop: "1rem" }}>
                    <DragDropContext onDragEnd={handleDragEnd}>
                      <Droppable droppableId="images" direction="horizontal">
                        {(provided) => (
                          <Box
                            display="flex"
                            gap={2}
                            mt={2}
                            flexWrap="wrap"
                            ref={provided.innerRef}
                            {...provided.droppableProps}
                          >
                            {values.images.length === 0 ? (
                              <Button
                                component="label"
                                sx={{
                                  border: "2px dashed gray",
                                  height: 200,
                                  width: "100%",
                                  display: "flex",
                                  flexDirection: "column",
                                  justifyContent: "center",
                                  alignItems: "center",
                                  borderRadius: "8px",
                                  backgroundColor: "transparent",
                                }}
                              >
                                <AddCircleIcon sx={{ fontSize: 40, color: "green" }} />
                                <Typography>Tap to Upload up to four photos</Typography>
                                <input
                                  hidden
                                  type="file"
                                  accept="image/*"
                                  onChange={handleImageUpload}
                                />
                              </Button>
                            ) : (
                              values.images.map((image: string, index: number) => (
                                <Draggable
                                  key={image}
                                  draggableId={image}
                                  index={index}
                                >
                                  {(provided) => (
                                    <Box
                                      ref={provided.innerRef}
                                      {...provided.draggableProps}
                                      {...provided.dragHandleProps}
                                      position="relative"
                                      flex={index === 0 ? "1 1 100%" : "1 1 30%"}
                                      maxWidth={index === 0 ? "100%" : "33%"}
                                      height={index === 0 ? 200 : 150}
                                    >
                                      <img
                                        src={`${awsImageRootUrl}/${image}`}
                                        alt={`thumb-${index}`}
                                        style={{
                                          width: "100%",
                                          height: "100%",
                                          objectFit: "cover",
                                          borderRadius: 8,
                                        }}
                                      />
                                      <IconButton
                                        size="small"
                                        sx={{
                                          position: "absolute",
                                          top: 4,
                                          right: 4,
                                          bgcolor: "white",
                                        }}
                                        onClick={() => handleRemoveImage(index)}
                                      >
                                        <CancelIcon fontSize="small" color="action" />
                                      </IconButton>
                                    </Box>
                                  )}
                                </Draggable>
                              ))
                            )}
                            {provided.placeholder}
                          </Box>
                        )}
                      </Droppable>
                    </DragDropContext>

                    {/* Upload More */}
                    {values.images.length > 0 && values.images.length < 4 && (
                      <Box mt={2}>
                        <Button
                          component="label"
                          variant="outlined"
                          sx={{ borderStyle: "dashed" }}
                        >
                          <AddCircleIcon sx={{ mr: 1 }} />
                          Upload more photos
                          <input
                            hidden
                            type="file"
                            accept="image/*"
                            onChange={handleImageUpload}
                          />
                        </Button>
                      </Box>
                    )}

                    <canvas
                      ref={canvasRef}
                      width={500}
                      height={500}
                      style={{ display: "none" }}
                    />

                    {errors.images && touched.images && (
                      <Typography color="red" fontSize="12px">
                        {errors.images}
                      </Typography>
                    )}
                  </Box>
                </Box>

                {/* Cropper Dialog */}
                <Dialog
                  open={openCropper}
                  onClose={() => setOpenCropper(false)}
                  maxWidth="sm"
                  fullWidth
                >
                  <DialogContent
                    sx={{ position: "relative", height: 400, background: "#333" }}
                  >
                    {imageSrc && (
                      <Cropper
                        image={imageSrc}
                        crop={crop}
                        zoom={zoom}
                        aspect={4 / 3} // You can adjust this aspect ratio for items
                        cropShape="rect"
                        rotation={0}
                        showGrid={true}
                        onCropChange={setCrop}
                        onZoomChange={setZoom}
                        onCropComplete={onCropComplete}
                      />
                    )}
                  </DialogContent>
                  <DialogActions>
                    <Button onClick={() => setOpenCropper(false)}>Cancel</Button>
                    <Button
                      variant="contained"
                      onClick={() => handleCropConfirm(setFieldValue)}
                    >
                      Crop & Upload
                    </Button>
                  </DialogActions>
                </Dialog>

                {/* Selling Points */}
                <Box sx={{ marginTop: "3rem" }}>
                  <Label
                    label="Selling Points and Important Info"
                    required={false}
                  />
                  <Typography>(Optional & Only Visible to Referees)</Typography>
                  <Field
                    as={TextField}
                    name="selling_points"
                    fullWidth
                    multiline
                    rows={3}
                    onChange={(e: any) => {
                      const text = e.target.value;
                      const lines = text.split("\n");

                      // Process each line
                      const processedLines = lines.map(
                        (line: any, index: number) => {
                          if (
                            line.trim() === "" &&
                            index !== lines.length - 1
                          ) {
                            return "";
                          }

                          const cleanLine = line.replace(/^•\s?/, "");

                          return cleanLine.trim() === ""
                            ? ""
                            : `• ${cleanLine}`;
                        }
                      );
                      const bulletText = processedLines.join("\n");
                      setFieldValue("selling_points", bulletText);
                      setFormValues((prevValues: any) => ({
                        ...prevValues,
                        selling_points: bulletText,
                      }));
                    }}
                  />
                  <PostScript text="Let Referees know a few key points that will help sell this item! You can also add things like price is negotiable or times are flexible." />
                </Box>

                {/* Industries */}
                <Box sx={{ marginTop: "1rem" }}>
                  <Label label="Select Industries" required={true} />
                  <Box display="flex" flexWrap="wrap" gap={1}>
                    {industries &&
                      industries?.map((industryType: Industry) => {
                        const _selected = selectedIndustry === industryType?.id;

                        return (
                          <Chip
                            key={industryType.id}
                            label={industryType.industry}
                            onClick={() =>
                              handleSelectIndustryCategory(industryType.id)
                            }
                            variant={_selected ? "filled" : "outlined"}
                            color={_selected ? "primary" : "default"}
                            sx={{
                              margin: "0 0.5rem 0.5rem 0",
                              border: _selected ? "none" : "1px solid #000",
                            }}
                          />
                        );
                      })}
                  </Box>
                  {errors.industry_ids && touched.industry_ids && (
                    <Typography color="red" fontSize="12px">
                      {errors.industry_ids}
                    </Typography>
                  )}
                </Box>

                {/* Categories */}
                <Box sx={{ marginTop: "1rem" }}>
                  <Label label="Select Categories" required={true} />
                  <Box display="flex" flexWrap="wrap" gap={1}>
                    {filteredCategories &&
                      filteredCategories?.map((category: Category) => {
                        const _selected = values.category_ids.includes(
                          category?.id
                        );

                        return (
                          <Chip
                            key={category.id}
                            label={category.category}
                            onClick={() => handleSelectCategory(category.id)}
                            variant={_selected ? "filled" : "outlined"}
                            color={_selected ? "primary" : "default"}
                            sx={{
                              margin: "0 0.5rem 0.5rem 0",
                              border: _selected ? "none" : "1px solid #000",
                              cursor: "pointer",
                            }}
                          />
                        );
                      })}
                  </Box>
                  {errors.category_ids && touched.category_ids && (
                    <Typography color="red" fontSize="12px">
                      {errors.category_ids}
                    </Typography>
                  )}
                </Box>

                {/* Description */}
                <Box>
                  <Label label="Description" required={true} />
                  <Field
                    as={TextField}
                    name="description"
                    fullWidth
                    multiline
                    rows={3}
                    onChange={handleChange}
                    error={touched.description && Boolean(errors.description)}
                  />
                  {errors.description && touched.description && (
                    <Typography color="red" fontSize="12px">
                      {errors.description}
                    </Typography>
                  )}
                  <PostScript text="3 to 5 sentences describing the item." />
                </Box>

                {/* Duration Picker */}
                <Box>
                  <Label label="Duration" required={true} />
                  {/* <DurationPicker /> */}
                  <DurationPicker
                    initialSelectedHours={values.duration_hours}
                    initialSelectedMinutes={values.duration_minutes}
                    onChange={handleDurationChange}
                    duration_applicable={values.duration_applicable}
                    handleDurationApplicableChange={
                      handleDurationApplicableChange
                    }
                    errors={errors}
                    touched={touched}
                  />
                </Box>

                {/* What's Included */}
                <Box>
                  <Label label="What's Included" required={true} />
                  <Field
                    as={TextField}
                    name="whats_included"
                    fullWidth
                    multiline
                    rows={3}
                    onChange={handleChange}
                    error={touched.whats_included && Boolean(errors.whats_included)}
                  />
                  {errors.whats_included && touched.whats_included && (
                    <Typography color="red" fontSize="12px">
                      {errors.whats_included}
                    </Typography>
                  )}
                  <PostScript text="Anything that is included with the base price of the item." />
                </Box>

                {/* Cancellation Policy */}
                <Box>
                  <Label label="Cancellation Policy" required={true} />
                  <Field
                    as={TextField}
                    name="cancellation_policy"
                    fullWidth
                    multiline
                    rows={3}
                    onChange={handleChange}
                    error={
                      touched.cancellation_policy &&
                      Boolean(errors.cancellation_policy)
                    }
                  />
                  {errors.cancellation_policy &&
                    touched.cancellation_policy && (
                      <Typography color="red" fontSize="12px">
                        {errors.cancellation_policy}
                      </Typography>
                    )}
                </Box>

                <Typography
                  sx={{ fontSize: "24px", fontWeight: "600", marginY: "10px" }}
                >
                  Optional Fields
                </Typography>

                {/* Additional Info */}
                <Box>
                  <Label label="Additional Info" required={false} />
                  <Field
                    as={TextField}
                    name="additional_info"
                    fullWidth
                    multiline
                    rows={3}
                    onChange={handleChange}
                    error={
                      touched.additional_info && Boolean(errors.additional_info)
                    }
                  />
                  {errors.additional_info && touched.additional_info && (
                    <Typography color="red" fontSize="12px">
                      {errors.additional_info}
                    </Typography>
                  )}
                  <PostScript
                    text="Any other information that you may have missed in 'Description' and 'Whats Included.'"
                    italics="Ex: What to bring, what to expect, etc."
                  />
                </Box>

                {/* Max No. of Guests */}
                <Box>
                  <Label label="Max Number of Guests" required={false} />
                  <Field
                    as={TextField}
                    type="number"
                    name="max_guest"
                    fullWidth
                    onChange={(e: any) => handleNumericChange(e, "max_guest")}
                    error={touched.max_guest && Boolean(errors.max_guest)}
                  />

                  {errors.max_guest && touched.max_guest && (
                    <Typography color="red" fontSize="12px">
                      {errors.max_guest}
                    </Typography>
                  )}
                  <PostScript text="Optional if you are selling individual tickets.  Enter max capacity if offering something like a boat charter or party bus." />
                </Box>

                {/* Min Max Age */}
                <Label label="Age Restrictions" required={false} />
                <Box sx={{ display: "flex", gap: "10px" }}>
                  {/* Minimum Age */}
                  <Box
                    sx={{ display: "flex", flexDirection: "column", flex: 1 }}
                  >
                    <Label label="Minimum Age" required={false} />
                    <Field
                      as={TextField}
                      type="numeric"
                      name="min_age"
                      fullWidth
                      onChange={(e: any) => handleNumericChange(e, "min_age")}
                      error={touched.min_age && Boolean(errors.min_age)}
                    />
                  </Box>

                  {/* Maximum Age */}
                  <Box
                    sx={{ display: "flex", flexDirection: "column", flex: 1 }}
                  >
                    <Label label="Maximum Age" required={false} />
                    <Field
                      as={TextField}
                      type="number"
                      name="max_age"
                      fullWidth
                      onChange={(e: any) => handleNumericChange(e, "max_age")}
                      error={touched.max_age && Boolean(errors.max_age)}
                    />
                  </Box>
                </Box>

                <Typography variant="h1" component="h1" paddingTop={"15px"}>
                  Edit Pricing and Options
                </Typography>

                <Typography
                  sx={{
                    color: "gray",
                    fontSize: "0.8rem",
                  }}
                >
                  *Required fields
                </Typography>

                {/* Base Price and Availablity Container */}
                <Box
                  sx={{ display: "flex", flexDirection: "row", gap: "10px" }}
                >
                  {/* Base Price */}
                  <Box
                    sx={{ display: "flex", flex: 1, flexDirection: "column" }}
                  >
                    <Label label="Base Price" required={true} />
                    <Field
                      as={TextField}
                      type="numeric"
                      name="price"
                      fullWidth
                      onChange={(e: any) => handleNumericChange(e, "price")}
                      error={touched.price && Boolean(errors.price)}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">$</InputAdornment>
                        ),
                      }}
                    />

                    {errors.price && touched.price && (
                      <Typography color="red" fontSize="12px">
                        {errors.price}
                      </Typography>
                    )}
                  </Box>

                  {/* Available */}
                  <Box
                    sx={{ display: "flex", flex: 1, flexDirection: "column" }}
                  >
                    <Label label="# Available" required={true} />
                    <Field
                      as={TextField}
                      type="numeric"
                      name="capacity"
                      fullWidth
                      onChange={(e: any) => handleNumericChange(e, "capacity")}
                      error={touched.capacity && Boolean(errors.capacity)}
                    />

                    {errors.capacity && touched.capacity && (
                      <Typography color="red" fontSize="12px">
                        {errors.capacity}
                      </Typography>
                    )}
                  </Box>
                </Box>

                {/* Pricing Description */}
                <Box>
                  <Label label="Pricing Description" required={false} />
                  <Field
                    as={TextField}
                    name="price_description"
                    fullWidth
                    onChange={handleChange}
                    error={
                      touched.price_description &&
                      Boolean(errors.price_description)
                    }
                  />
                  {errors.price_description && touched.price_description && (
                    <Typography color="red" fontSize="12px">
                      {errors.price_description}
                    </Typography>
                  )}
                  <PostScript
                    text="Explain your pricing model if you have variable pricing or add-ons."
                    italics="Ex: $1000 up to 10 people then an additional $100/person over. Ex: $100/single jet ski or $120/double jet ski"
                  />
                </Box>

                {/* Unit Name */}
                <Box>
                  <Label label="Unit Name" required={true} />
                  <Field
                    as={TextField}
                    name="unit_name"
                    fullWidth
                    onChange={handleChange}
                    error={touched.unit_name && Boolean(errors.unit_name)}
                  />
                  {errors.unit_name && touched.unit_name && (
                    <Typography color="red" fontSize="12px">
                      {errors.unit_name}
                    </Typography>
                  )}
                  <PostScript italics="Ex: Boat, Jet skis, tickets, etc. " />
                </Box>

                {/* Commission Rate */}
                <Box>
                  <Label label="Commission Rate" required={true} />
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "row",
                      alignItems: "center",
                      gap: "10px",
                    }}
                  >
                    <Box sx={{ display: "flex", flex: 1 }}>
                      <Field
                        as={TextField}
                        name="comission"
                        fullWidth
                        onChange={(e: any) =>
                          handleNumericChange(e, "comission")
                        }
                        error={touched.comission && Boolean(errors.comission)}
                      />
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        width: "50px",
                        border: "1px solid black",
                        justifyContent: "center",
                        paddingY: "15px",
                        borderRadius: "5px",
                      }}
                    >
                      <img src={PercentageIcon} alt="age-icon" />
                    </Box>
                  </Box>

                  {errors.comission && touched.comission && (
                    <Typography color="red" fontSize="12px">
                      {errors.comission}
                    </Typography>
                  )}
                </Box>

                {/* Payout box */}
                <Box
                  sx={{
                    marginTop: "10px",
                    borderRadius: "8px",
                    backgroundColor: "#D0EED5",
                    padding: "10px",
                  }}
                >
                  <Typography
                    sx={{
                      color: "#1C1B1C",
                      fontSize: "17px",
                      fontWeight: "700",
                    }}
                  >
                    Your Pay Out Before Add-Ons:
                  </Typography>
                  <Typography
                    sx={{
                      color: "#1C1B1C",
                      fontSize: "17px",
                      fontWeight: "400",
                    }}
                  >
                    {calculatePayoffPrice(
                      Number(values.price),
                      Number(values.comission)
                    )}
                  </Typography>
                </Box>

                {/* Customer Added Value */}
                <Box>
                  <Label label="Customer Added Value" required={false} />
                  <Field
                    as={TextField}
                    name="offering"
                    fullWidth
                    onChange={handleChange}
                    multiline
                    rows={3}
                    error={touched.offering && Boolean(errors.offering)}
                  />
                  {errors.offering && touched.offering && (
                    <Typography color="red" fontSize="12px">
                      {errors.offering}
                    </Typography>
                  )}

                  <PostScript
                    text="Great way to help encourage the booking."
                    italics="Ex: Free bottle of champagne or price discount explaining normal price and the now discounted price."
                  />
                </Box>

                {/* Options and Add-Ons */}
                <Box>
                  <Typography
                    sx={{
                      fontSize: "24px",
                      fontWeight: "700",
                      paddingTop: "20px",
                      paddingBottom: "10px",
                    }}
                  >
                    Options and Add-Ons
                  </Typography>
                  <PostScript text="If you have add-ons or need more information from the client please select one of the option types below." />

                  {/* Buttons Container */}
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "15px",
                      marginY: "10px",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "row",
                        gap: "10px",
                      }}
                    >
                      <Button
                        sx={{
                          display: "flex",
                          flex: 1,
                          border: "1px solid black",
                          fontSize: "17px",
                          color: "#000",
                          fontWeight: "600",
                          boxShadow: "0 4px 4px rgba(0, 0, 0, 0.25)",
                          paddingY: "12px",
                        }}
                        onClick={() => setVisibleCheckboxModal(true)}
                      >
                        Check Box
                      </Button>
                      <Button
                        sx={{
                          display: "flex",
                          flex: 1,
                          border: "1px solid black",
                          fontSize: "17px",
                          color: "#000",
                          fontWeight: "600",
                          boxShadow: "0 4px 4px rgba(0, 0, 0, 0.25)",
                          paddingY: "12px",
                        }}
                        onClick={() => setVisibleTextModal(true)}
                      >
                        Text Field
                      </Button>
                    </Box>
                    <Box sx={{ width: "100%" }}>
                      <Button
                        sx={{
                          display: "flex",
                          flex: 1,
                          border: "1px solid black",
                          width: "100%",
                          fontSize: "17px",
                          color: "#000",
                          fontWeight: "600",
                          boxShadow: "0 4px 4px rgba(0, 0, 0, 0.25)",
                          paddingY: "12px",
                        }}
                        onClick={() => setVisibleDropdownModal(true)}
                      >
                        Quantity Dropdown
                      </Button>
                    </Box>
                  </Box>

                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "row",
                      alignItems: "baseline",
                      gap: "2px",
                    }}
                  >
                    <Typography
                      sx={{
                        color: "#595959",
                        fontSize: "15px",
                        fontWeight: "700",
                      }}
                    >
                      *Note{" "}
                    </Typography>
                    <Typography
                      sx={{
                        color: "#595959",
                        fontSize: "15px",
                        fontWeight: "700",
                      }}
                    >
                      -
                    </Typography>
                    <PostScript italics=" Every checkout link collects customer name, email, phone, group type and optional comments." />
                  </Box>
                </Box>

                {/* Additional Information */}
                <Box>
                  {values?.additionalOptions &&
                    values?.additionalOptions?.length > 0 &&
                    values?.additionalOptions?.map(
                      (item: CreateAdditionalOptionDto, index: any) => (
                        <Box
                          key={index}
                          sx={{
                            border: "1px solid #595959",
                            padding: "10px",
                            borderRadius: "8px",
                            marginY: "10px",
                          }}
                        >
                          {/* Header */}
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "row",
                              justifyContent: "space-between",
                              alignItems: "center",
                            }}
                          >
                            <Typography
                              sx={{
                                fontSize: "17px",
                                color: "#000",
                                fontWeight: "700",
                              }}
                            >
                              {item.type === 1
                                ? "Text Field"
                                : item.type === 2
                                  ? "Quantity Dropdown"
                                  : "Check Box"}
                            </Typography>
                            <IconButton
                              onClick={() => {
                                handleRemoveAdditionalOption(index);
                              }}
                            >
                              <CloseIcon />
                            </IconButton>
                          </Box>

                          {/* Body */}
                          {item?.type === 1 ? (
                            <>
                              <Typography
                                sx={{
                                  marginTop: "0px",
                                  fontSize: "17px",
                                  fontWeight: "600",
                                  paddingY: "5px",
                                }}
                              >
                                {item?.header}
                                {item?.required && "*"}
                              </Typography>
                              <Field
                                as={TextField}
                                name="place_holder"
                                placeholder={item?.place_holder ?? ""}
                                fullWidth
                              />
                            </>
                          ) : item?.type === 2 ? (
                            <>
                              <Typography
                                sx={{
                                  marginTop: "0px",
                                  fontSize: "17px",
                                  fontWeight: "600",
                                  paddingY: "5px",
                                }}
                              >
                                {item?.header}
                                {item?.required && "*"}
                              </Typography>

                              <Box
                                sx={{
                                  display: "flex",
                                  flexDirection: "row",
                                  alignItems: "center",
                                  border: "1px solid #000",
                                  borderRadius: "8px",
                                  padding: "15px",
                                  justifyContent: "space-between",
                                }}
                              >
                                <Typography
                                  sx={{
                                    fontSize: "16px",
                                    color: "#000",
                                    fontWeight: "400",
                                  }}
                                >
                                  {item?.availability}
                                </Typography>
                                <Box
                                  sx={{
                                    display: "flex",
                                    flexDirection: "row",
                                    gap: "20px",
                                    alignItems: "center",
                                  }}
                                >
                                  {/* Price */}
                                  <Typography
                                    sx={{
                                      fontSize: "16px",
                                      color: "#000",
                                      fontWeight: "600",
                                    }}
                                  >
                                    ${item?.price}
                                  </Typography>

                                  <ArrowDropDownIcon />
                                </Box>
                              </Box>
                            </>
                          ) : (
                            <Box
                              sx={{
                                border: "1px solid #E0E0E0",
                                borderRadius: "8px",
                                padding: "10px",
                                display: "flex",
                                flexDirection: "row",
                                alignItems: "center",
                                marginBottom: "10px",
                              }}
                            >
                              {/* Left side: Checkbox */}
                              <Box sx={{ marginRight: "10px" }}>
                                <Checkbox name="isRequired" />
                              </Box>

                              {/* Middle section: Content */}
                              <Box sx={{ flex: 1 }}>
                                <Box
                                  sx={{
                                    display: "flex",
                                    flexDirection: "row",
                                    justifyContent: "space-between",
                                  }}
                                >
                                  <Typography
                                    sx={{
                                      marginTop: "0px",
                                      fontSize: "17px",
                                      fontWeight: "600",
                                    }}
                                  >
                                    {item?.header}
                                    {item?.required && "*"}
                                  </Typography>
                                  <Typography
                                    sx={{
                                      marginTop: "0px",
                                      fontSize: "17px",
                                      fontWeight: "700",
                                    }}
                                  >
                                    ${item?.price}
                                  </Typography>
                                </Box>

                                <Typography
                                  sx={{
                                    fontSize: "15px",
                                    color: "#595959",
                                    marginTop: "0px",
                                    fontWeight: "400",
                                  }}
                                >
                                  {item?.description}
                                </Typography>
                              </Box>
                            </Box>
                          )}
                        </Box>
                      )
                    )}
                </Box>

                <Button
                  type="submit"
                  variant="contained"
                  sx={{ width: "100%", marginTop: "20px", paddingY: "10px" }}
                >
                  Save Changes
                </Button>

                {/* AdOnCheckBoxModal */}
                <AdOnCheckBoxModal
                  open={visibleCheckboxModal}
                  handleClose={() => setVisibleCheckboxModal(false)}
                  setFormValues={setFormValues}
                  onSave={handleOptionSave}
                />

                {/* AdOnCheckBoxModal */}
                <AdOnTextModal
                  open={visibleTextModal}
                  handleClose={() => setVisibleTextModal(false)}
                  setFormValues={setFormValues}
                  onSave={handleOptionSave}
                />

                {/* AdOnDropdownModal */}
                <AdOnDropdownModal
                  open={visibleDropdownModal}
                  handleClose={() => setVisibleDropdownModal(false)}
                  setFormValues={setFormValues}
                  onSave={handleOptionSave}
                />

                {/* SaveItemModal */}
                <SaveItemModal
                  open={visibleSaveModal}
                  close={() => setVisibleSaveModal(false)}
                  loading={loader}
                  onSubmit={handleEditSubmit}
                // customStyle={{ bgcolor: "lightblue" }}
                // sx={{}}
                />
              </Box>
            </Form>
          );
        }}
      </Formik>
    </Box>
  );
};

export default EditItemForm;